import { isSameDay } from 'date-fns'
import { array, func, number, object, string } from 'prop-types'
import { Component } from 'react'
import {
  kuiperClassNames,
  TrackedFilterBar,
  MenuSection,
  TrackedScrollableMenu,
  SlideOutPanel,
  StatelessModal as Modal,
} from '@opallabs/kuiper'
import {
  getLocalStorage,
  localStorageParentKeys,
  setLocalStorage,
} from '@opallabs/principles'
import { AnalyticsLayer } from '@opallabs/quanta'
import PlacementsHeader from '../PlacementsHeader'
import './styles.scss'

const NO_CONTENT_MESSAGE = 'No content placed on this day'
const EMPTY_PLACEMENT_CLASS = 'empty-placement'
const COMPOSITIONAL_KEY = 'placements-previewer-compositional'
const FILTER_KEY = 'placements-previewer-filters'

const interpolatedClassnames = (ids = []) =>
  ids.reduce((acc, pair) => {
    acc.push(
      <div
        className={kuiperClassNames(
          `content-card-${pair.postId}`,
          'content-card'
        )}
        key={pair.postId}
      />
    )
    return acc
  }, [])

const menuSectionContent = (idPairs) => {
  if (idPairs.length > 0) {
    return interpolatedClassnames(idPairs)
  }

  return (
    <div className={kuiperClassNames(EMPTY_PLACEMENT_CLASS)}>
      {NO_CONTENT_MESSAGE}
    </div>
  )
}

const hasFilteredLabel = (labels, enabledFilters) =>
  enabledFilters.some((filter) =>
    labels.some((label) => `label-${label.id}` === filter)
  )

class PlacementsPreviewer extends Component {
  static propTypes = {
    account: object,
    accountId: string,
    brandId: number,
    content: array,
    date: object,
    exportContentModal: func,
    fetchIds: func,
    filterTree: array,
    getAccount: func,
    getContent: func,
    getLabels: func,
    idPairs: array,
    navigateToDate: func,
    placementContentMap: object,
    placements: array,
    renderCards: func,
    service: object,
    startOfWeek: number,
    unmountModal: func,
  }

  static defaultProps = {
    content: [],
    fetchIds: () => {},
    filterTree: [],
    getAccount: () => {},
    getContent: () => {},
    getLabels: () => {},
    navigateToDate: () => {},
    placements: [],
    renderCards: () => {},
    exportContentModal: () => {},
  }

  constructor(props) {
    super(props)

    this.contentStartingToday = []
    this.newContentToProcess = !!props.content.length

    const contentParams = {
      filter: {
        account_ids: [this.props.accountId],
        brand_id: this.props.brandId,
        scheduled_end_at: this.props.date,
        scheduled_start_at: this.props.date,
      },
      include: ['placement', 'brand', 'account', 'service', 'moment'],
    }

    this.props.getContent(contentParams)
    this.props.getAccount({
      accountId: this.props.accountId,
      include: 'placements',
    })
    this.props.getLabels({ brandId: this.props.brandId })
    this.state = {
      activeFilterCount: 0,
      allFiltersDisabled: false,
      chromeless: false,
      disabledFilterValues: [],
      enabledFilterValues: [],
      filterBarVisible: false,
      modalOpen: true,
      seamless: false,
    }
  }

  componentDidMount() {
    this.getPersistedCompositionalSettings()
    this.getPersistedFilterSettings()
    this.renderContentCards()
  }

  shouldComponentUpdate(nextProps, nextState) {
    const newEntitiesIncoming = this.newEntitiesIncoming(nextProps)

    if (newEntitiesIncoming) {
      this.findContentStartingToday(nextProps.content)
    }

    const styleChangesIncoming = this.styleChangesIncoming(nextState)

    const toggleFilterBarVisibility = this.toggleFilterBarVisibility(nextState)

    const newFiltersIncoming = this.newFiltersIncoming(nextState)

    return (
      newEntitiesIncoming ||
      styleChangesIncoming ||
      toggleFilterBarVisibility ||
      newFiltersIncoming
    )
  }

  componentDidUpdate() {
    this.renderContentCards()
  }

  renderContentCards = () => {
    if (this.props.idPairs.length > 0) {
      const { chromeless, seamless } = this.state
      this.props.renderCards(seamless, chromeless)
      this.newContentToProcess = false
      this.setPlacements()
    }
  }

  toggleFilterBar = () => {
    this.setState(
      (prevState) => ({
        filterBarVisible: !prevState.filterBarVisible,
      }),
      () => {
        this.persistFilterSettings()
      }
    )
  }

  get placementsData() {
    const unordered = this.props.placements.map((placement) => ({
      name: placement.name,
      position: placement.position,
      id: placement.id,
      idPairs: this.props.placementContentMap[placement.id] || [],
    }))
    const sorted = unordered.sort((a, b) => {
      if (a.position === b.position) {
        return 0
      }
      if (a.position === undefined || a.position === null) {
        return 1
      }
      if (b.position === undefined || b.position === null) {
        return -1
      }
      return a.position > b.position ? 1 : -1
    })
    return sorted
  }

  newEntitiesIncoming = (nextProps) => {
    const currentPlacementIds = this.props.placements
      .map((placement) => placement.id)
      .sort()
    const nextPlacementIds = nextProps.placements
      .map((placement) => placement.id)
      .sort()
    const currentContentIds = this.props.content
      .map((content) => content.id)
      .sort()
    const nextPropsContentIds = nextProps.content
      .map((content) => content.id)
      .sort()
    let newPlacementsIncoming = false
    let newContentsIncoming = false

    if (nextPlacementIds.length !== currentPlacementIds.length) return true
    if (nextPropsContentIds.length !== currentContentIds.length) {
      this.newContentToProcess = true
      return true
    }

    if (nextPlacementIds.length > 0) {
      newPlacementsIncoming = nextPlacementIds.every(
        (val, idx) => val !== currentPlacementIds[idx]
      )
    }

    if (nextPropsContentIds.length > 0) {
      newContentsIncoming = nextPropsContentIds.every(
        (val, idx) => val !== currentContentIds[idx]
      )
      this.newContentToProcess = newContentsIncoming
    }

    return newPlacementsIncoming || newContentsIncoming
  }

  newFiltersIncoming = (nextState) => {
    if (this.state.allFiltersDisabled !== nextState.allFiltersDisabled) {
      return true
    }

    if (
      this.state.enabledFilterValues.length === 0 &&
      nextState.enabledFilterValues.length === 0
    ) {
      return false
    }

    if (
      this.state.disabledFilterValues.length !==
        nextState.disabledFilterValues.length ||
      this.state.enabledFilterValues.length !==
        nextState.enabledFilterValues.length
    ) {
      return true
    }

    return false
  }

  toggleFilterBarVisibility = (nextState) =>
    nextState.filterBarVisible !== this.state.filterBarVisible

  styleChangesIncoming = (nextState) =>
    nextState.chromeless !== this.state.chromeless ||
    nextState.seamless !== this.state.seamless

  closeModal = () => {
    this.setState(
      {
        modalOpen: false,
      },
      () => this.props.unmountModal()
    )
  }

  setPlacements = () => {
    this.setState({
      placements: this.props.placements,
    })
  }

  getPersistedFilterSettings = () => {
    const persistedFilterSettings = getLocalStorage({
      namespace: localStorageParentKeys.PREVIEWER,
      key: FILTER_KEY,
      parse: true,
    })

    if (persistedFilterSettings) {
      this.setState({
        enabledFilterValues: persistedFilterSettings.enabledFilterValues,
        disabledFilterValues: persistedFilterSettings.disabledFilterValues,
        activeFilterCount: persistedFilterSettings.activeFilterCount,
        filterBarVisible: persistedFilterSettings.filterBarVisible,
        allFiltersDisabled: persistedFilterSettings.allFiltersDisabled,
      })
    }
  }

  getPersistedCompositionalSettings = () => {
    const persistedCompositionalSettings = getLocalStorage({
      namespace: localStorageParentKeys.PREVIEWER,
      key: COMPOSITIONAL_KEY,
      parse: true,
    })

    if (persistedCompositionalSettings) {
      this.setState({
        seamless: persistedCompositionalSettings.seamless,
        chromeless: persistedCompositionalSettings.chromeless,
      })
    }
  }

  findContentStartingToday = (contents) => {
    this.contentStartingToday = []
    contents.forEach((content) => {
      const scheduledAt = new Date(content.scheduled_at)
      if (isSameDay(this.props.date, scheduledAt)) {
        this.contentStartingToday.push(content.id)
      }
    })
  }

  displayStatusDot = (idPairs) =>
    idPairs.some((pair) => this.contentStartingToday.includes(pair.postId))

  persistFilterSettings = () => {
    const filtersToStore = {
      enabledFilterValues: this.state.enabledFilterValues,
      disabledFilterValues: this.state.disabledFilterValues,
      activeFilterCount: this.state.activeFilterCount,
      filterBarVisible: this.state.filterBarVisible,
      allFiltersDisabled: this.state.allFiltersDisabled,
    }

    setLocalStorage({
      namespace: localStorageParentKeys.PREVIEWER,
      key: FILTER_KEY,
      stringify: true,
      value: filtersToStore,
    })
  }

  persistCompositionalSettings = () => {
    const compositionalSettings = {
      chromeless: this.state.chromeless,
      seamless: this.state.seamless,
    }

    setLocalStorage({
      namespace: localStorageParentKeys.PREVIEWER,
      key: COMPOSITIONAL_KEY,
      stringify: true,
      value: compositionalSettings,
    })
  }

  onChromeChange = (value) => {
    if (value.title === 'Content only' && !this.state.chromeless) {
      this.setState({ chromeless: true }, () => {
        this.persistCompositionalSettings()
      })
    } else if (value.title === 'Content card' && this.state.chromeless) {
      this.setState({ chromeless: false }, () => {
        this.persistCompositionalSettings()
      })
    }
  }

  onSeamlessChange = (value) => {
    if (value.title === 'Seamless' && !this.state.seamless) {
      this.setState({ seamless: true, chromeless: true }, () => {
        this.persistCompositionalSettings()
      })
    } else if (value.title === 'Column' && this.state.seamless) {
      this.setState({ seamless: false }, () => {
        this.persistCompositionalSettings()
      })
    }
  }

  get filteredContentIds() {
    const enabledFilters = this.state.enabledFilterValues.filter(
      (value) => !this.state.disabledFilterValues.includes(value)
    )

    if (!enabledFilters.length) {
      return this.props.content.map((content) => content.id)
    }

    return this.props.content
      .filter((content) => {
        const mappedStatus =
          {
            for_approval: 'pending',
            needs_revision: 'denied',
          }[content.status] || content.status

        const status = `status-${mappedStatus}`
        const combinedLabels = [].concat(
          content.labels,
          content.inherited_labels
        )

        return (
          enabledFilters.includes(status) ||
          hasFilteredLabel(combinedLabels, enabledFilters) ||
          this.state.allFiltersDisabled
        )
      })
      .map((content) => content.id)
  }

  updateFilters = (selectedFilters, metaFilters = {}) => {
    const allFiltersDisabled = !!metaFilters.disabled
    const disabledValues =
      (metaFilters.value && metaFilters.value.disabled) || []
    let enabledFilters

    if (metaFilters.value) {
      enabledFilters = selectedFilters.filter(
        (filter) => !allFiltersDisabled && !disabledValues.includes(filter.id)
      )
    } else {
      enabledFilters = selectedFilters
    }

    this.setState(
      {
        activeFilterCount: enabledFilters.length,
        disabledFilterValues: disabledValues,
        enabledFilterValues: selectedFilters.map((filter) => filter.id),
        allFiltersDisabled,
      },
      () => {
        this.persistFilterSettings()
      }
    )
  }

  get renderPlaced() {
    const includedContentIds = this.filteredContentIds

    return this.placementsData.map((placement) => {
      const filteredIdPairs = placement.idPairs.filter((idPair) =>
        includedContentIds.includes(idPair.postId)
      )

      return (
        <MenuSection
          customClassName={kuiperClassNames('placements-placed')}
          key={`section-${placement.name}-${placement.id}`}
          sidebarStatusDotPresent={this.displayStatusDot(filteredIdPairs)}
          sidebarStatusDotStatus="new"
          pillContents={`${filteredIdPairs.length}`}
          title={placement.name}
        >
          {menuSectionContent(filteredIdPairs)}
        </MenuSection>
      )
    })
  }

  get renderUnplaced() {
    const includedContentIds = this.filteredContentIds
    const { unplaced } = this.props.placementContentMap
    const filteredUnplacedIdPairs = unplaced.filter((idPair) =>
      includedContentIds.includes(idPair.postId)
    )
    const unplacedCount = filteredUnplacedIdPairs.length

    return (
      <MenuSection
        customClassName={kuiperClassNames('placements-unplaced')}
        key="section-unplaced"
        pillContents={`${unplacedCount}`}
        sidebarStatusDotPresent={this.displayStatusDot(filteredUnplacedIdPairs)}
        sidebarStatusDotStatus="new"
        title="Unplaced Content"
      >
        {menuSectionContent(filteredUnplacedIdPairs)}
      </MenuSection>
    )
  }

  get menuSections() {
    return [...this.renderPlaced, this.renderUnplaced]
  }

  get enabledFilterValues() {
    return this.state.enabledFilterValues
  }

  previewArea = (children, customClassName, menuProps) => (
    <div
      {...menuProps}
      className={kuiperClassNames('placements-preview-area', customClassName)}
    >
      {children}
    </div>
  )

  render() {
    this.findContentStartingToday(this.props.content)

    if (this.props.idPairs.length > 0 && this.newContentToProcess) {
      this.props.fetchIds(this.props.idPairs)
    }

    return (
      <AnalyticsLayer name="Placements Previewer">
        <Modal
          headerContents="Placement Preview"
          customClassname="placements-container"
          onClose={this.closeModal}
          visible={this.state.modalOpen}
        >
          <SlideOutPanel theme="350" visible={this.state.filterBarVisible}>
            <TrackedFilterBar
              filtersDisabled={this.state.allFiltersDisabled}
              theme="dark"
              disabledValues={this.state.disabledFilterValues}
              value={this.enabledFilterValues}
              filterTree={this.props.filterTree}
              onClose={this.toggleFilterBar}
              onChange={this.updateFilters}
            />
          </SlideOutPanel>
          <div className={kuiperClassNames('content-area')}>
            <PlacementsHeader
              {...this.props}
              chromeless={this.state.chromeless}
              navigateToDate={this.props.navigateToDate}
              onChromeChange={this.onChromeChange}
              onSeamlessChange={this.onSeamlessChange}
              exportContentModal={this.props.exportContentModal}
              seamless={this.state.seamless}
              startOfWeek={this.props.startOfWeek}
              activeFilterCount={this.state.activeFilterCount}
              toggleFilterBar={this.toggleFilterBar}
              filterButtonVisible={!this.state.filterBarVisible}
            />
            <TrackedScrollableMenu
              disableSubmit={true}
              disableHighlighting={true}
              menuSections={this.menuSections}
              previewArea={this.previewArea}
              previewerCustomClassNames={[
                { chromeless: this.state.chromeless },
                { seamless: this.state.seamless },
              ]}
            />
          </div>
        </Modal>
      </AnalyticsLayer>
    )
  }
}

export default PlacementsPreviewer
